/**
 * DelegationMessageCard — renders the two messages one agent asking another
 * leaves behind: the question in the consult session, and the answer in the
 * session that asked.
 *
 * Both are `role:'user'` rows the PLATFORM composed, not the nurse. Rendered as
 * an ordinary user bubble they read as her own words, which is why they get the
 * shared hand-off surface instead, in the assistant column rather than hers.
 *
 * The card writes its OWN copy from the message's attributes, in the reader's
 * language. The message text itself is written for the model and is English by
 * contract, so relaying it verbatim would put English platform prose in front
 * of a nurse working in German.
 */

import { type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CornerUpLeft, Send } from 'lucide-react'

import { TURN_FAIL_QUOTA_EXCEEDED } from '@/api/constants'
import { navigateToSession } from '@/lib/sessionRouting'
import type { DelegationMessage } from '@/lib/delegationMarker'
import { HandoffCardShell } from './HandoffCardShell'
import { useHandoffAgentLabel } from './useHandoffAgentLabel'
import { MarkdownContent } from './MarkdownContent'

interface DelegationMessageCardProps {
  delegation: DelegationMessage
  isLatest?: boolean
}

export function DelegationMessageCard({
  delegation,
  isLatest,
}: DelegationMessageCardProps): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const handoffAgentLabel = useHandoffAgentLabel()
  const isReply = delegation.kind === 'delegation_reply'

  // A failed consult has no answer to show, only the fact that there is none.
  const failed = isReply && Boolean(delegation.failed)
  let labelKey = 'chat.delegation.askedBy'
  if (failed) labelKey = 'chat.delegation.couldNotAnswer'
  else if (isReply) labelKey = 'chat.delegation.answeredBy'
  const label = t(labelKey, { agent: handoffAgentLabel(delegation.agent) })

  return (
    <HandoffCardShell
      render="delegation"
      dataAttributes={{ 'data-delegation-kind': delegation.kind }}
      icon={isReply ? CornerUpLeft : Send}
      label={label}
      isLatest={isLatest}
      // Only a reply names a consult, and only then is there somewhere to send
      // the reader. A specialist that answers with a card leaves its verdict
      // there, so this link is how the nurse reaches it.
      action={
        isReply && delegation.sessionId
          ? {
              label: t('chat.delegation.openConsult'),
              onClick: () =>
                navigateToSession(
                  { id: delegation.sessionId as string, agent: { slug: delegation.agent, version: '' } },
                  navigate,
                ),
            }
          : undefined
      }
    >
      {failed ? t(failedHintKey(delegation.failed)) : <MarkdownContent content={delegation.body} />}
    </HandoffCardShell>
  )
}

// What the reader can do about a failed consult. A spent model quota has an
// answer of its own (wait, then ask again); anything else is read in the
// consult, where it stopped.
function failedHintKey(reason: string | undefined): string {
  return reason === TURN_FAIL_QUOTA_EXCEEDED ? 'chat.turnFailedQuota' : 'chat.delegation.seeConsult'
}
